import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import classnames from "classnames";
import { isEmpty } from "lodash";
import SubmitButton from "components/Forms/Buttons/Submit";
import Notification from "components/Notification";
import Permission from "components/hoc/Permission";
import Translate from "helpers/Translate";
import {
  BULK_SUBMIT_REVIEW,
  ADD_COMMENTS,
  UPLOAD_DOCS_TO_ADAPTER
} from "helpers/permissions.json";
import {
  updateStatus,
  getBulkReviews,
  saveBulkReview,
  clearBulkReview
} from "store/actions/fileAction";
import {
  STATUS_APPROVED,
  STATUS_READY_TO_UPLOAD,
  STATUS_SENT
} from "helpers/fileStatus.json";

export const BulkReview = () => {
  const dispatch = useDispatch();
  const bulkReviewFiles = useSelector(state => state.file.getBulkReviewFiles);
  const selectedClient = useSelector(state => state.client.selectedClient);
  const updateFileResponseStatus = useSelector(state => state.file.httpStatus);

  const [files, setFiles] = useState(null);
  const [totalSent, setTotalSent] = useState(0);
  const [totalSelected, setTotalSelected] = useState(0);
  const permissions = useSelector(state => state.role.getPermissions);
  const [comment, setComment] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Load the bulk review files
  useEffect(() => {
    if (!bulkReviewFiles) {
      dispatch(getBulkReviews());
    }
  }, [bulkReviewFiles, dispatch]);

  // Add bulk review file to the state
  useEffect(() => {
    if (files === null && bulkReviewFiles) {
      setFiles(bulkReviewFiles);
    }
  }, [files, bulkReviewFiles, dispatch]);

  useEffect(() => {
    if (updateFileResponseStatus === 200) {
      setIsUploading(false);
    }
  }, [updateFileResponseStatus]);

  const handleSend = async () => {
    if (!files.length) {
      return Notification.warning(
        Translate({ id: "please_select_atleast_one_file" })
      );
    }

    setIsUploading(true);
    let totalSent = 0;
    const fileIds = [];

    for (const file of files) {
      if (!file.selected || !isEmpty(file.success)) continue;
      file.selected = false;
      file.status = STATUS_SENT;
      fileIds.push(file.id);
      totalSent++;
    } //for-loop ends

    const status = canUploadToAdapter()
      ? STATUS_READY_TO_UPLOAD
      : STATUS_APPROVED;
    dispatch(
      updateStatus({
        clientId: selectedClient.id,
        data: {
          status,
          files: fileIds,
          comment
        }
      })
    );

    if (totalSent) {
      setComment("");

      // Get list of files that were not sent
      const unsentFiles = files.filter(f => !fileIds.includes(f.id));
      setTotalSent(totalSent);
      setTotalSelected(0);
      dispatch(
        saveBulkReview({
          fileDocs: unsentFiles,
          isDirectory: false,
          flushOld: true
        })
      );
    }
  };

  const canUploadToAdapter = () => {
    return permissions && permissions.includes(UPLOAD_DOCS_TO_ADAPTER);
  };

  return (
    <div className="container bulk-container">
      <h2 className="title mb-30">{Translate({ id: "send_bulk_review" })} :</h2>

      <Permission resources={[BULK_SUBMIT_REVIEW]}>
        <>
          <div className="stats-wrapper my-20">
            <div className="mb-5">
              <strong className="text-dark">
                {Translate({ id: "total_files" })} :
              </strong>{" "}
              <span className="total pointer">
                {bulkReviewFiles ? bulkReviewFiles.length : 0}
              </span>{" "}
            </div>
            <div className="mb-5">
              <strong className="text-dark">
                {Translate({ id: "selected" })} :
              </strong>{" "}
              <span className="total ">{totalSelected}</span>{" "}
            </div>
            <div className="mb-5">
              <strong className="text-dark">
                {Translate({ id: "success" })} :
              </strong>{" "}
              <span className="uploaded">{totalSent}</span>
            </div>
          </div>

          {/* No files to send */}
          {files === null && (
            <h5 className="mt-40">{Translate({ id: "no_files_to_send" })} </h5>
          )}

          {/* No files found when filtered */}
          {files && !files.length && (
            <h5 className="mt-40">{Translate({ id: "no_files_found" })} </h5>
          )}

          {files && files.length > 0 && (
            <span>
              <hr className="my-20" />
              <Permission
                message={Translate({
                  id: "you_do_not_have_permission_to_add_a_comment"
                })}
                resources={[ADD_COMMENTS]}
              >
                <>
                  <label htmlFor="comment">
                    {Translate({ id: "comment" })}{" "}
                  </label>
                  <textarea
                    className="form-input"
                    id="comment"
                    cols="30"
                    rows="10"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                  ></textarea>
                </>
              </Permission>

              <div className="mb-10"></div>

              <SubmitButton
                disabled={!totalSelected}
                className="btn btn-info btn-full"
                buttonIconClass="fas fa-paper-plane mr-5"
                buttonText={Translate({
                  id: "bulk_send_files",
                  values: [totalSelected]
                })}
                btnId="send"
                isLoading={isUploading}
                handleSubmit={handleSend}
              />
              <hr className="my-20" />

              <button
                className="btn btn-danger btn-full mb-10"
                onClick={() => {
                  Notification.confirm(
                    Translate({ id: "this_will_clear_the_list_continue" }),
                    {
                      labelOk: Translate({ id: "yes" }),
                      labelCancel: Translate({ id: "no" }),
                      onOk: () => {
                        setTotalSelected(0);
                        setFiles(false);
                        dispatch(clearBulkReview());
                      }
                    }
                  );
                }}
              >
                {Translate({ id: "remove_all" })}
              </button>

              <label htmlFor="all">
                <input
                  type="checkbox"
                  id="all"
                  className="mr-5"
                  onClick={e => {
                    if (files && e.target.checked) {
                      files.forEach(file => {
                        file.selected = true;
                      });
                      setTotalSelected(files.length);
                    } else {
                      files.forEach(file => {
                        file.selected = false;
                      });
                      setTotalSelected(0);
                    }
                  }}
                />{" "}
                {Translate({ id: "select_all" })}
              </label>

              <hr className="my-10" />
              <ul className="folders">
                {files &&
                  files.length > 0 &&
                  files.map((file, idx) => (
                    <li key={idx}>
                      <label className="row">
                        <div className="checkbox mr-10">
                          <input
                            disabled={!isEmpty(file.success)}
                            type="checkbox"
                            name="bulk-review"
                            id={file.id}
                            checked={
                              isEmpty(file.success) && (file.selected || false)
                            }
                            onChange={e => {
                              file.selected = e.target.checked;
                              if (e.target.checked) {
                                setTotalSelected(
                                  prevSelected => prevSelected + 1
                                );
                              } else {
                                setTotalSelected(
                                  prevSelected => prevSelected - 1
                                );
                              }
                            }}
                          />
                        </div>
                        <div className="icon mr-5">
                          <i className="fas fa-file"></i>
                        </div>
                        <div className="name">{file.raw_filename} </div>
                      </label>

                      <p title={file.path} className="path">
                        {file.relative_path}
                      </p>

                      {/* Error, Success or current status message */}
                      <div
                        className={classnames("row", {
                          "mt-10": isUploading
                        })}
                      >
                        {isUploading && (
                          <>
                            <i className="fas fa-cloud-upload-alt color-info mr-5"></i>
                            <span className="color-info name">
                              {Translate({ id: "updating" })}{" "}
                            </span>
                          </>
                        )}
                      </div>

                      <hr className="my-20" />
                    </li>
                  ))}
              </ul>
            </span>
          )}
        </>
      </Permission>
    </div>
  );
};

export default BulkReview;
